import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, X, Pencil, Trash2, Check } from 'lucide-react';
import type { AIPromptTemplate } from '@xiaotianshu/shared';

interface PromptTemplatesProps {
  onSelectPrompt: (content: string) => void;
  disabled?: boolean;
  dropdownDirection?: 'up' | 'down';
}

const PromptTemplates: React.FC<PromptTemplatesProps> = ({ onSelectPrompt, disabled, dropdownDirection = 'up' }) => {
  const [templates, setTemplates] = useState<AIPromptTemplate[]>([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editContent, setEditContent] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newContent, setNewContent] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const data = await window.api.getAIPromptTemplates();
      setTemplates(data as AIPromptTemplate[]);
    } catch { /* 静默降级 */ }
  }, []);

  useEffect(() => {
    if (open) fetchTemplates();
  }, [open, fetchTemplates]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setAdding(false);
        setEditingId(null);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [open]);

  const handleSelect = (content: string) => {
    onSelectPrompt(content);
    setOpen(false);
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      await window.api.createAIPromptTemplate({ name: newName.trim(), content: newContent });
      setNewName('');
      setNewContent('');
      setAdding(false);
      await fetchTemplates();
    } catch { /* 静默降级 */ }
  };

  const handleDelete = async (id: string) => {
    try {
      await window.api.deleteAIPromptTemplate(id);
      await fetchTemplates();
    } catch { /* 静默降级 */ }
  };

  const startEdit = (t: AIPromptTemplate) => {
    setEditingId(t.id);
    setEditName(t.name);
    setEditContent(t.content);
  };

  const handleUpdate = async () => {
    if (editingId === null || !editName.trim()) return;
    try {
      await window.api.updateAIPromptTemplate({ id: editingId, name: editName.trim(), content: editContent });
      setEditingId(null);
      await fetchTemplates();
    } catch { /* 静默降级 */ }
  };

  return (
    <div className="ai-prompt-templates" ref={containerRef}>
      <button
        className="ai-prompt-trigger-btn"
        onClick={() => setOpen(v => !v)}
        disabled={disabled}
        title="提示词模板"
        type="button"
      >
        <span className="ai-prompt-trigger-icon">📋</span>
        <span className="ai-prompt-trigger-text">提示词</span>
      </button>

      {open && (
        <div className={`ai-prompt-dropdown ai-prompt-dropdown--${dropdownDirection}`}>
          <div className="ai-prompt-dropdown-header">
            <span className="ai-prompt-dropdown-title">提示词列表</span>
            <button
              className="ai-prompt-add-btn"
              onClick={() => { setAdding(true); setEditingId(null); }}
              title="新建提示词"
              type="button"
            >
              <Plus size={14} />
            </button>
          </div>

          <div className="ai-prompt-dropdown-list">
            {templates.length === 0 && !adding && (
              <div className="ai-prompt-empty">暂无提示词，点击 + 新建</div>
            )}

            {templates.map((t) => (
              <div key={t.id} className="ai-prompt-item">
                {editingId === t.id ? (
                  <div className="ai-prompt-edit-form">
                    <input
                      className="ai-prompt-edit-name"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      placeholder="提示词名称"
                      autoFocus
                    />
                    <textarea
                      className="ai-prompt-edit-content"
                      value={editContent}
                      onChange={e => setEditContent(e.target.value)}
                      placeholder="提示词内容"
                      rows={2}
                    />
                    <div className="ai-prompt-edit-actions">
                      <button
                        className="ai-prompt-edit-save"
                        onClick={handleUpdate}
                        title="保存"
                        type="button"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        className="ai-prompt-edit-cancel"
                        onClick={() => setEditingId(null)}
                        title="取消"
                        type="button"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      className="ai-prompt-item-main"
                      onClick={() => handleSelect(t.content)}
                      title={t.content}
                      type="button"
                    >
                      <span className="ai-prompt-item-name">{t.name}</span>
                      <span className="ai-prompt-item-preview">
                        {t.content.length > 40 ? t.content.slice(0, 40) + '…' : t.content}
                      </span>
                    </button>
                    <button
                      className="ai-prompt-item-edit"
                      onClick={() => startEdit(t)}
                      title="编辑"
                      type="button"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      className="ai-prompt-item-delete"
                      onClick={() => handleDelete(t.id)}
                      title="删除"
                      type="button"
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                )}
              </div>
            ))}

            {adding && (
              <div className="ai-prompt-item ai-prompt-item--new">
                <div className="ai-prompt-edit-form">
                  <input
                    className="ai-prompt-edit-name"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder="提示词名称"
                    autoFocus
                  />
                  <textarea
                    className="ai-prompt-edit-content"
                    value={newContent}
                    onChange={e => setNewContent(e.target.value)}
                    placeholder="提示词内容"
                    rows={3}
                  />
                  <div className="ai-prompt-edit-actions">
                    <button
                      className="ai-prompt-edit-save"
                      onClick={handleAdd}
                      disabled={!newName.trim()}
                      title="保存"
                      type="button"
                    >
                      <Check size={14} />
                    </button>
                    <button
                      className="ai-prompt-edit-cancel"
                      onClick={() => { setAdding(false); setNewName(''); setNewContent(''); }}
                      title="取消"
                      type="button"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptTemplates;
